// Interface copy is grouped by intent so every component stays presentation-only.
export const translations = {
  en: {
    locale: 'en-GB',
    seo: {
      title: 'Afshin Zehtab — Full-Stack Developer & Digital Designer',
      description: 'Portfolio of Afshin Zehtab, a Munich-based full-stack developer and IHK-certified digital media designer building thoughtful web products.',
    },
    nav: { about: 'About', work: 'Selected work', experience: 'Experience', contact: 'Contact' },
    actions: { menu: 'Open menu', close: 'Close menu', theme: 'Switch colour theme', visit: 'Visit live project', top: 'Back to top' },
    hero: {
      availability: 'Available for select projects',
      eyebrow: 'Full-stack developer · Digital designer',
      line1: 'I build digital',
      line2: 'experiences that',
      line3: 'feel inevitable.',
      summary: 'Engineering precision meets visual instinct. I turn complex ideas into clear, fast and genuinely useful web products.',
      ctaWork: 'Explore my work',
      ctaContact: 'Start a conversation',
      location: 'Based in Munich · Working worldwide',
      scroll: 'Scroll to discover',
    },
    ticker: ['Product thinking', 'Web engineering', 'Interface design', 'Creative direction'],
    about: {
      kicker: 'Beyond the code',
      title: 'A developer who thinks in systems—and cares about the smallest detail.',
      body1: 'I am Afshin, a full-stack web developer and IHK-certified digital media designer. For more than a decade I have moved between code, design and real-world operations—learning how good products serve both people and business.',
      body2: 'My work is direct, considered and built to last. I care about strong architecture, accessible interfaces and the quiet details that make a product feel effortless.',
      stat1: 'Years shaping the web',
      stat2: 'Languages spoken',
      stat3: 'Live selected products',
      signature: 'Code with purpose. Design with character.',
    },
    projects: {
      kicker: 'Selected work · 2025—2026',
      title: 'Products with a pulse.',
      intro: 'A selection of platforms, business tools and digital experiences I have designed and developed.',
      live: 'Live',
      open: 'Open project',
    },
    experience: {
      kicker: 'Career path',
      title: 'Built through code, craft and leadership.',
      intro: 'My experience is not a straight line—and that is its strength. It combines technical depth with the perspective to lead people and deliver under real constraints.',
    },
    skills: {
      kicker: 'Capabilities',
      title: 'From first sketch to shipped system.',
      frontend: 'Frontend systems',
      backend: 'Backend & data',
      product: 'Product & design',
      tooling: 'Delivery & quality',
      education: 'Education',
      languages: 'Languages',
    },
    contact: {
      kicker: 'Let’s make something useful',
      title: 'Have a role, a project or an ambitious idea?',
      intro: 'Tell me what you are building, where you need help and what a great outcome looks like. I will get back to you personally.',
      name: 'Your name',
      email: 'Email address',
      subject: 'What is this about?',
      message: 'Tell me a little about the project…',
      send: 'Send message',
      sending: 'Sending…',
      success: 'Thank you — your message is on its way. I will reply soon.',
      error: 'The message could not be sent. Please email me directly instead.',
      privacy: 'No mailing lists. No noise. Just a direct reply.',
      verification: 'Security verification',
      verificationHint: 'Verification starts when you use the form.',
      verificationError: 'Security verification could not load. Please refresh the page.',
      verificationRejected: 'Security verification failed. Please refresh the page and try again.',
      rateLimitError: 'Too many attempts were detected. Please wait a while before trying again.',
      direct: 'Prefer email?',
    },
    footer: { note: 'Designed and engineered with intention in Munich.', rights: 'All rights reserved.' },
  },
  de: {
    locale: 'de-DE',
    seo: {
      title: 'Afshin Zehtab — Full-Stack-Entwickler & Digital Designer',
      description: 'Portfolio von Afshin Zehtab, Full-Stack-Entwickler und IHK-Mediengestalter aus München für durchdachte digitale Produkte.',
    },
    nav: { about: 'Über mich', work: 'Projekte', experience: 'Erfahrung', contact: 'Kontakt' },
    actions: { menu: 'Menü öffnen', close: 'Menü schließen', theme: 'Farbschema wechseln', visit: 'Live-Projekt besuchen', top: 'Nach oben' },
    hero: {
      availability: 'Verfügbar für ausgewählte Projekte',
      eyebrow: 'Full-Stack-Entwickler · Digital Designer',
      line1: 'Ich entwickle digitale',
      line2: 'Erlebnisse, die sich',
      line3: 'richtig anfühlen.',
      summary: 'Technische Präzision trifft visuelles Gespür. Ich verwandle komplexe Ideen in klare, schnelle und wirklich nützliche Webprodukte.',
      ctaWork: 'Projekte entdecken',
      ctaContact: 'Gespräch beginnen',
      location: 'In München · Weltweit im Einsatz',
      scroll: 'Scrollen zum Entdecken',
    },
    ticker: ['Produktdenken', 'Webentwicklung', 'Interface Design', 'Creative Direction'],
    about: {
      kicker: 'Mehr als Code',
      title: 'Ein Entwickler, der in Systemen denkt—und jedes Detail ernst nimmt.',
      body1: 'Ich bin Afshin, Full-Stack-Webentwickler und IHK-zertifizierter Mediengestalter. Seit über zehn Jahren bewege ich mich zwischen Code, Design und realer Betriebsführung und lerne dabei, wie gute Produkte Menschen und Unternehmen zugleich unterstützen.',
      body2: 'Meine Arbeit ist direkt, durchdacht und auf Langlebigkeit ausgelegt. Mir sind starke Architekturen, zugängliche Interfaces und jene leisen Details wichtig, die ein Produkt mühelos wirken lassen.',
      stat1: 'Jahre Web-Erfahrung',
      stat2: 'Gesprochene Sprachen',
      stat3: 'Ausgewählte Live-Produkte',
      signature: 'Code mit Sinn. Design mit Charakter.',
    },
    projects: {
      kicker: 'Ausgewählte Projekte · 2025—2026',
      title: 'Produkte mit Puls.',
      intro: 'Eine Auswahl an Plattformen, Business-Tools und digitalen Erlebnissen, die ich gestaltet und entwickelt habe.',
      live: 'Live',
      open: 'Projekt öffnen',
    },
    experience: {
      kicker: 'Berufsweg',
      title: 'Gewachsen durch Code, Gestaltung und Führung.',
      intro: 'Mein Weg ist nicht geradlinig – und genau darin liegt seine Stärke. Er verbindet technische Tiefe mit der Fähigkeit, Menschen zu führen und unter realen Bedingungen zu liefern.',
    },
    skills: {
      kicker: 'Kompetenzen',
      title: 'Von der ersten Skizze bis zum fertigen System.',
      frontend: 'Frontend-Systeme',
      backend: 'Backend & Daten',
      product: 'Produkt & Design',
      tooling: 'Delivery & Qualität',
      education: 'Ausbildung',
      languages: 'Sprachen',
    },
    contact: {
      kicker: 'Lassen Sie uns etwas Sinnvolles schaffen',
      title: 'Eine Position, ein Projekt oder eine ambitionierte Idee?',
      intro: 'Erzählen Sie mir, was Sie entwickeln, wo Unterstützung nötig ist und wie ein starkes Ergebnis aussieht. Ich antworte persönlich.',
      name: 'Ihr Name',
      email: 'E-Mail-Adresse',
      subject: 'Worum geht es?',
      message: 'Erzählen Sie mir etwas über das Projekt …',
      send: 'Nachricht senden',
      sending: 'Wird gesendet …',
      success: 'Vielen Dank — Ihre Nachricht ist unterwegs. Ich melde mich bald.',
      error: 'Die Nachricht konnte nicht gesendet werden. Schreiben Sie mir bitte direkt per E-Mail.',
      privacy: 'Keine Verteiler. Kein Lärm. Nur eine direkte Antwort.',
      verification: 'Sicherheitsprüfung',
      verificationHint: 'Die Prüfung startet, sobald Sie das Formular verwenden.',
      verificationError: 'Die Sicherheitsprüfung konnte nicht geladen werden. Bitte laden Sie die Seite neu.',
      verificationRejected: 'Die Sicherheitsprüfung ist fehlgeschlagen. Bitte laden Sie die Seite neu und versuchen Sie es erneut.',
      rateLimitError: 'Es wurden zu viele Versuche erkannt. Bitte warten Sie etwas und versuchen Sie es später erneut.',
      direct: 'Lieber per E-Mail?',
    },
    footer: { note: 'Mit Anspruch in München gestaltet und entwickelt.', rights: 'Alle Rechte vorbehalten.' },
  },
  fa: {
    locale: 'fa-IR',
    seo: {
      title: 'افشین زهتاب — توسعه‌دهنده فول‌استک و طراح دیجیتال',
      description: 'وب‌سایت شخصی افشین زهتاب، توسعه‌دهنده فول‌استک و طراح رسانه دیجیتال در مونیخ؛ سازنده محصولات حرفه‌ای وب.',
    },
    nav: { about: 'درباره من', work: 'پروژه‌ها', experience: 'تجربه‌ها', contact: 'ارتباط' },
    actions: { menu: 'باز کردن منو', close: 'بستن منو', theme: 'تغییر تم رنگی', visit: 'مشاهده پروژه زنده', top: 'بازگشت به بالا' },
    hero: {
      availability: 'آماده همکاری در پروژه‌های منتخب',
      eyebrow: 'توسعه‌دهنده فول‌استک · طراح دیجیتال',
      line1: 'تجربه‌های دیجیتالی',
      line2: 'می‌سازم که درست و',
      line3: 'اجتناب‌ناپذیر به نظر می‌رسند.',
      summary: 'دقت مهندسی در کنار نگاه بصری؛ ایده‌های پیچیده را به محصولات وب شفاف، سریع و واقعاً کاربردی تبدیل می‌کنم.',
      ctaWork: 'دیدن پروژه‌ها',
      ctaContact: 'شروع گفتگو',
      location: 'مستقر در مونیخ · همکاری در سراسر جهان',
      scroll: 'برای کشف بیشتر اسکرول کنید',
    },
    ticker: ['تفکر محصول', 'مهندسی وب', 'طراحی رابط', 'هدایت خلاقانه'],
    about: {
      kicker: 'فراتر از کد',
      title: 'توسعه‌دهنده‌ای که سیستمی فکر می‌کند و کوچک‌ترین جزئیات برایش مهم است.',
      body1: 'من افشین هستم؛ توسعه‌دهنده فول‌استک وب و طراح رسانه دیجیتال دارای مدرک IHK آلمان. بیش از ده سال میان کد، طراحی و مدیریت واقعی حرکت کرده‌ام و آموخته‌ام یک محصول خوب چگونه هم به انسان و هم به کسب‌وکار خدمت می‌کند.',
      body2: 'کار من شفاف، سنجیده و ماندگار است. معماری قوی، رابط‌های در دسترس و جزئیات ظریفی که محصول را بی‌دردسر نشان می‌دهند برایم اهمیت دارند.',
      stat1: 'سال تجربه در وب',
      stat2: 'زبان قابل گفتگو',
      stat3: 'محصول زنده منتخب',
      signature: 'کد با هدف. طراحی با شخصیت.',
    },
    projects: {
      kicker: 'پروژه‌های منتخب · ۲۰۲۵—۲۰۲۶',
      title: 'محصولاتی با نبض زنده.',
      intro: 'منتخبی از پلتفرم‌ها، ابزارهای تجاری و تجربه‌های دیجیتال که طراحی و توسعه داده‌ام.',
      live: 'آنلاین',
      open: 'باز کردن پروژه',
    },
    experience: {
      kicker: 'مسیر حرفه‌ای',
      title: 'ساخته‌شده با کد، خلاقیت و رهبری.',
      intro: 'مسیر من خطی نیست و قدرت آن دقیقاً همین‌جاست؛ ترکیبی از عمق فنی، هدایت انسان‌ها و ارائه نتیجه در شرایط واقعی.',
    },
    skills: {
      kicker: 'توانایی‌ها',
      title: 'از نخستین طرح تا یک سیستم آماده انتشار.',
      frontend: 'سیستم‌های فرانت‌اند',
      backend: 'بک‌اند و داده',
      product: 'محصول و طراحی',
      tooling: 'انتشار و کیفیت',
      education: 'تحصیلات',
      languages: 'زبان‌ها',
    },
    contact: {
      kicker: 'بیایید چیزی مفید بسازیم',
      title: 'یک موقعیت شغلی، پروژه یا ایده‌ای بلندپروازانه دارید؟',
      intro: 'بگویید چه چیزی می‌سازید، کجا به کمک نیاز دارید و نتیجه ایده‌آل شما چیست. شخصاً پاسخ خواهم داد.',
      name: 'نام شما',
      email: 'آدرس ایمیل',
      subject: 'موضوع گفتگو چیست؟',
      message: 'کمی درباره پروژه برایم بنویسید…',
      send: 'ارسال پیام',
      sending: 'در حال ارسال…',
      success: 'سپاس — پیام شما ارسال شد. به‌زودی پاسخ می‌دهم.',
      error: 'پیام ارسال نشد. لطفاً مستقیماً برایم ایمیل بفرستید.',
      privacy: 'بدون خبرنامه و پیام اضافه؛ فقط یک پاسخ مستقیم.',
      verification: 'بررسی امنیتی',
      verificationHint: 'بررسی امنیتی با شروع تکمیل فرم فعال می‌شود.',
      verificationError: 'بررسی امنیتی بارگذاری نشد. لطفاً صفحه را دوباره بارگذاری کنید.',
      verificationRejected: 'بررسی امنیتی ناموفق بود. صفحه را تازه‌سازی کرده و دوباره تلاش کنید.',
      rateLimitError: 'تعداد تلاش‌ها بیش از حد مجاز بود. کمی صبر کنید و دوباره تلاش کنید.',
      direct: 'ایمیل را ترجیح می‌دهید؟',
    },
    footer: { note: 'با دقت در مونیخ طراحی و توسعه داده شده است.', rights: 'تمام حقوق محفوظ است.' },
  },
};
